import React from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { StickyContainer } from 'react-sticky';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

import Navigation from './navigation';
import themeDefault from './UIKit/theme';
import { fontFamilySans, getThemeColor } from '../utils/styles';
import Footer from './footer';
import SEO from './seo';
import Responsive from './responsive';
import GlobalCtaBlock from './global-cta-block';
import { SearchStateProvider } from '../context/search';
import { SearchWrap } from './global-search';

const GlobalStyle = createGlobalStyle`
  :root {
    --font-mulish: 'Mulish', 'Helvetica Neue', Helvetica, sans-serif, Arial;
    --Color-White: #FFFFFFFF;
    --Color-Grey-05: #F8F8F8FF;
    --Color-Grey-1: #DEE2E4FF;
    --Color-Grey-2: #CBC9C9FF;
    --Color-Grey-3: #A8A8A8FF;
    --Color-Grey-4: #757575FF;
    --Color-Grey-5: #565656FF;
    --Color-Grey-6: #2D2D2DFF;
    --Color-Grey-7: #111111FF;
    --Color-Black: #000000FF;
    --Color-Purple-1: #CAADFFFF;
    --Color-Purple-2: #A270FFFF;
    --Color-Purple-3: #813DFFFF;
    --Color-Purple-4: #5616CAFF;
    --Color-Purple-5: #2B007AFF;
    --Color-White-Metal-1: #EEEDF2FF;
    --Color-White-Metal-2: #E3E3E6FF;
    --Color-White-Metal-3: #BEBEC5FF;
    --Color-White-Metal-4: #93939FFF;
    --Color-White-Metal-5: #60606CFF;
    --Color-Rock-Blue-1: #92A3B5FF;
    --Color-Rock-Blue-2: #6D849CFF;
    --Color-Rock-Blue-3: #4B5C6EFF;
    --Color-Rock-Blue-4: #323D49FF;
    --Color-Rock-Blue-5: #212931FF;
    --Color-Boost-Green-1: #B6E2C6FF;
    --Color-Boost-Green-2: #8AD0A4FF;
    --Color-Boost-Green-3: #59BD7DFF;
    --Color-Boost-Green-4: #40A063FF;
    --Color-Boost-Green-5: #2F7548FF;
    --Color-Sky-Blue-1: #C5E3E7FF;
    --Color-Sky-Blue-2: #9ACED5FF;
    --Color-Sky-Blue-3: #7EC0C9FF;
    --Color-Sky-Blue-4: #52ABB7FF;
    --Color-Sky-Blue-5: #3C8690FF;
    --Color-Volt-Orange-1: #F7BFA1FF;
    --Color-Volt-Orange-2: #F39968FF;
    --Color-Volt-Orange-3: #F07F42FF;
    --Color-Volt-Orange-4: #ED651DFF;
    --Color-Volt-Orange-5: #D05411FF;
    --Radius-Radius-full: 9999px;
    --Radius-Radius-1: 4px;
    --Radius-Radius-2: 8px;
    --Radius-Radius-3: 16px;
    --Radius-Radius-4: 24px;
    --Foreground-fg-primary: var(--Color-Grey-7);
    --Foreground-fg-secondary: var(--Color-Grey-5);
    --Foreground-fg-primary-inverse: var(--Color-White);
    --Foreground-fg-secondary-inverse: var(--Color-Grey-3);
    --Foreground-fg-interactive: var(--Color-Purple-3);
    --Foreground-fg-interactive-inverse: var(--Color-White);
    --Foreground-fg-accent: var(--Color-Purple-3);
    --Foreground-fg-accent-inverse: var(--Color-Purple-2);
    --Background-bg-primary: var(--Color-White);
    --Background-bg-secondary: var(--Color-Grey-05);
    --Background-bg-inverse: var(--Color-Grey-7);
    --Background-bg-interactive: var(--Color-Purple-3);
    --Background-bg-interactive-inverse: var(--Color-Purple-3);
    --Border-border-primary: var(--Color-Grey-2);
    --Border-border-secondary: var(--Color-Grey-1);
    --Border-border-inverse: var(--Color-Grey-5);
    --Border-border-interactive: var(--Color-Purple-3);
    --Border-border-interactive-inverse: var(--Color-Purple-3);
  }

  body {
    ${fontFamilySans};
    color: ${getThemeColor('black')};
  }

  *, *:before, *:after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 0;
  }
`;

const Wrapper = styled.div`
  overflow-x: hidden;
`;

function Layout({
  children,
  seoTitle,
  seoDescription,
  seoSharingImage,
  seoPathname,
  navigationEditorContent,
  footerEditorContent,
  noCtaBlock,
  searchData
}) {
  return (
    <ThemeProvider theme={themeDefault}>
      <StickyContainer>
        <Responsive.Provider>
          <SEO
            title={seoTitle}
            description={seoDescription}
            sharingImage={seoSharingImage}
            pathname={seoPathname}
          />
          <GlobalStyle />
          <Wrapper>
            <SearchStateProvider>
              <Navigation
                editorContent={navigationEditorContent}
                searchData={searchData}
              />
              <SearchWrap searchData={searchData}>{children}</SearchWrap>
            </SearchStateProvider>
            {!noCtaBlock && <GlobalCtaBlock />}
            <Footer footerEditorContent={footerEditorContent} />
          </Wrapper>
        </Responsive.Provider>
      </StickyContainer>
    </ThemeProvider>
  );
}

export default Layout;
